<template>
  <div>
    <div class="flex flex-col gap-3 mb-4 sm:mb-6 sm:flex-row sm:justify-between sm:items-center">
      <h2 class="text-xl sm:text-2xl font-bold text-gray-900">Mitgliedsstrafen verwalten</h2>
      <div class="flex gap-2 sm:gap-4">
        <input
          v-model="selectedYear"
          type="number"
          @change="loadMemberPunishments"
          class="w-24 sm:w-28 border border-gray-300 rounded-md px-3 py-2 text-black"
          placeholder="Jahr"
        />
        <button
          @click="openCreateModal"
          class="inline-flex flex-1 sm:flex-none items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Neue Mitgliedsstrafe
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-8">Laden...</div>
    <div v-else-if="error" class="rounded-md bg-red-50 p-4 mb-4">
      <p class="text-sm text-red-800">{{ error }}</p>
    </div>
    <div v-else>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th class="px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mitglied</th>
              <th class="px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Strafe</th>
              <th class="px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wert</th>
              <th class="px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
              <th class="sticky right-0 bg-gray-50 px-3 py-3 sm:px-6 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="mp in memberPunishments" :key="mp.id">
              <td class="hidden sm:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ mp.id }}</td>
              <td class="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">
                {{ mp.member ? `${mp.member.firstName} ${mp.member.lastName}` : mp.memberId }}
              </td>
              <td class="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">
                {{ mp.punishment ? mp.punishment.type : mp.punishmentId }}
              </td>
              <td class="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{{ mp.value }}</td>
              <td class="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{{ formatDate(mp.date) }}</td>
              <td class="sticky right-0 bg-white px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-right text-sm font-medium">
                <button
                  @click="openEditModal(mp)"
                  class="text-indigo-600 hover:text-indigo-900 mr-3 sm:mr-4"
                >
                  Bearbeiten
                </button>
                <button
                  @click="confirmDelete(mp)"
                  class="text-red-600 hover:text-red-900"
                >
                  Löschen
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="fixed z-10 inset-0 overflow-y-auto" @click.self="closeModal">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form @submit.prevent="saveMemberPunishment">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                {{ editingMemberPunishment ? 'Mitgliedsstrafe bearbeiten' : 'Neue Mitgliedsstrafe' }}
              </h3>
              <div v-if="formError" class="rounded-md bg-red-50 p-3 mb-4">
                <p class="text-sm text-red-800">{{ formError }}</p>
              </div>
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700">Mitglied</label>
                  <select
                    v-model.number="formData.memberId"
                    required
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-black focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Bitte wählen</option>
                    <option v-for="member in members" :key="member.id" :value="member.id">
                      {{ member.firstName }} {{ member.lastName }}
                    </option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Strafe</label>
                  <select
                    v-model.number="formData.punishmentId"
                    @change="onPunishmentChange"
                    required
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-black focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Bitte wählen</option>
                    <option v-for="punishment in punishments" :key="punishment.id" :value="punishment.id">
                      {{ punishment.type }} ({{ punishment.value }})
                    </option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Wert</label>
                  <input
                    v-model.number="formData.value"
                    type="number"
                    required
                    min="0"
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-black focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Datum</label>
                  <input
                    v-model="formData.date"
                    type="date"
                    required
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-black focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
            <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Speichern
              </button>
              <button
                type="button"
                @click="closeModal"
                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import adminService, { type AdminMemberPunishment, type AdminMember, type AdminPunishment } from '@/services/AdminService'

const memberPunishments = ref<AdminMemberPunishment[]>([])
const members = ref<AdminMember[]>([])
const punishments = ref<AdminPunishment[]>([])
const loading = ref(false)
const error = ref('')
const formError = ref('')
interface MemberPunishmentFormData {
  memberId: number
  punishmentId: number
  value: number
  date: string
}

// Helper function to get today's date as string
const getTodayDateString = (): string => {
  return new Date().toISOString().split('T')[0] as string
}

const showModal = ref(false)
const editingMemberPunishment = ref<AdminMemberPunishment | null>(null)
const selectedYear = ref(new Date().getFullYear().toString())
const formData = ref<MemberPunishmentFormData>({
  memberId: 0,
  punishmentId: 0,
  value: 0,
  date: getTodayDateString()
})

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('de-DE')
}

const loadMemberPunishments = async () => {
  loading.value = true
  error.value = ''
  try {
    memberPunishments.value = await adminService.getMemberPunishments(selectedYear.value)
  } catch (err: any) {
    error.value = err.response?.data?.error || 'Fehler beim Laden der Mitgliedsstrafen'
  } finally {
    loading.value = false
  }
}

const loadMembers = async () => {
  try {
    members.value = await adminService.getMembers()
  } catch (err: any) {
    console.error('Error loading members:', err)
  }
}

const loadPunishments = async () => {
  try {
    punishments.value = await adminService.getPunishments()
  } catch (err: any) {
    console.error('Error loading punishments:', err)
  }
}

const openCreateModal = async () => {
  await Promise.all([loadMembers(), loadPunishments()])
  editingMemberPunishment.value = null
  formData.value = {
    memberId: 0,
    punishmentId: 0,
    value: 0,
    date: getTodayDateString()
  }
  showModal.value = true
}

const openEditModal = async (mp: AdminMemberPunishment) => {
  await Promise.all([loadMembers(), loadPunishments()])
  editingMemberPunishment.value = mp
  formData.value = {
    memberId: mp.memberId,
    punishmentId: mp.punishmentId,
    value: mp.value,
    date: mp.date.split('T')[0] as string
  }
  showModal.value = true
}

const closeModal = () => {
  formError.value = ''
  showModal.value = false
  editingMemberPunishment.value = null
}

const onPunishmentChange = () => {
  if (formData.value.punishmentId && !editingMemberPunishment.value) {
    // Nur beim Erstellen, nicht beim Bearbeiten
    const selectedPunishment = punishments.value.find(p => p.id === formData.value.punishmentId)
    if (selectedPunishment) {
      formData.value.value = selectedPunishment.value
    }
  }
}

const saveMemberPunishment = async () => {
  formError.value = ''
  try {
    const payload = {
      memberId: formData.value.memberId,
      punishmentId: formData.value.punishmentId,
      value: formData.value.value,
      date: formData.value.date
    }
    
    if (editingMemberPunishment.value) {
      await adminService.updateMemberPunishment(editingMemberPunishment.value.id, payload)
    } else {
      await adminService.createMemberPunishment(payload)
    }
    closeModal()
    await loadMemberPunishments()
  } catch (err: any) {
    formError.value = err.response?.data?.error || 'Fehler beim Speichern der Mitgliedsstrafe'
  }
}

const confirmDelete = async (mp: AdminMemberPunishment) => {
  if (confirm('Möchten Sie diese Mitgliedsstrafe wirklich löschen?')) {
    try {
      await adminService.deleteMemberPunishment(mp.id)
      await loadMemberPunishments()
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Fehler beim Löschen der Mitgliedsstrafe'
    }
  }
}

onMounted(() => {
  loadMemberPunishments()
  loadMembers()
  loadPunishments()
})
</script>

